import type React from "react";

import { Button } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import type { OperationColor } from "./constants";

export interface BulkOperation {
  color?: OperationColor;
  disabled?: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  id: string;
  label: string;
  onClick: () => void;
}

export interface BulkOperationsToolbarProps {
  className?: string;
  isLoading?: boolean;
  operations: BulkOperation[];
  selectedCount: number;
  statusDropdown?: React.ReactNode;
  variant?: "compact" | "embedded";
}

/**
 * Gets the appropriate CSS classes for a bulk operation button based on its state
 */
const getOperationButtonClasses = (
  hasSelection: boolean,
  isDisabled: boolean,
  isLoading: boolean,
  color?: OperationColor,
) => {
  const baseClasses =
    "flex items-center gap-2 bg-button-secondary-bg px-3 py-1.5 text-sm rounded-md border shadow-sm transition-colors";

  if (!hasSelection || isDisabled || isLoading) {
    return twMerge(
      baseClasses,
      "border-border-secondary text-fg-disabled cursor-not-allowed",
    );
  }

  if (color === "destructive") {
    return twMerge(
      baseClasses,
      "border-border-primary text-button-secondary-error-fg hover:bg-button-secondary-error-bg-hover cursor-pointer",
    );
  }

  return twMerge(
    baseClasses,
    "border-border-primary text-button-secondary-fg hover:bg-button-secondary-bg-hover cursor-pointer",
  );
};

/**
 * Formats the selection count text with proper pluralization
 */
const getSelectionText = (count: number): string =>
  count === 1 ? "1 item selected" : `${count} items selected`;

export function BulkOperationsToolbar({
  className,
  isLoading = false,
  operations,
  selectedCount,
  statusDropdown,
  variant = "embedded",
}: BulkOperationsToolbarProps) {
  const hasSelection = selectedCount > 0;

  // Variant-specific classes
  const variantClasses =
    variant === "embedded"
      ? "bg-bg-secondary px-4 py-3 border-b border-border-secondary transition-all duration-200 font-semibold"
      : "";

  // Render compact variant (team-lead)
  if (variant === "compact") {
    return (
      <div className={twMerge("flex items-center gap-3", className)}>
        {hasSelection && (
          <span className="text-xs text-text-tertiary">
            Press ESC to clear selection
          </span>
        )}

        <div className="flex items-center gap-3">
          {operations.map((operation) => {
            const isOperationDisabled =
              !hasSelection || operation.disabled || isLoading;

            return (
              <Button
                className={twMerge(
                  getOperationButtonClasses(
                    hasSelection,
                    Boolean(operation.disabled),
                    isLoading,
                    operation.color,
                  ),
                  "min-w-[140px] font-medium",
                )}
                isDisabled={isOperationDisabled}
                key={operation.id}
                onPress={operation.onClick}
              >
                <operation.icon height={16} strokeWidth={2} width={16} />
                {operation.label}
              </Button>
            );
          })}

          {statusDropdown}
        </div>
      </div>
    );
  }

  // Render embedded variant (customer - original behavior)
  return (
    <div
      className={twMerge(
        "flex items-center justify-between",
        variantClasses,
        className,
      )}
    >
      <div className="flex items-center gap-2">
        {operations.map((operation) => {
          const isOperationDisabled =
            !hasSelection || operation.disabled || isLoading;

          return (
            <Button
              className={getOperationButtonClasses(
                hasSelection,
                Boolean(operation.disabled),
                isLoading,
                operation.color,
              )}
              isDisabled={isOperationDisabled}
              key={operation.id}
              onPress={operation.onClick}
            >
              <operation.icon height={16} strokeWidth={2} width={16} />
              {operation.label}
            </Button>
          );
        })}
      </div>

      {hasSelection && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-primary">
            {getSelectionText(selectedCount)}
          </span>
          <span className="text-xs text-text-tertiary">
            Press ESC to clear selection
          </span>
        </div>
      )}
    </div>
  );
}
